import { z } from 'zod';
export const ScenarioClassificationResponseSchema = z.object({
    scenarioIndex: z
        .number()
        .nullable()
        .describe('0-based index of the matching scenario, or null if no match'),
    confidence: z
        .number()
        .min(0)
        .max(1)
        .describe('Confidence score from 0.0 to 1.0'),
    reason: z
        .string()
        .describe('Brief explanation of why this scenario was selected or why none matched'),
});
export const scenarioClassificationPrompt = {
    name: 'scenario-classification',
    version: 'v1.1',
    systemPrompt: `You are a classifier. Given a conversation transcript and a numbered list of scenarios with their selection criteria, choose the single best matching scenario or respond with no match.

Confidence scoring guide:
- High confidence (>0.9): The conversation directly matches the scenario criteria with no significant deviations. Example: Scenario criteria is "unstoppable water leak" and the caller reports "water is leaking and cannot be stopped".
- Medium confidence (0.6–0.8): The conversation is highly similar to the scenario criteria but may have minor deviations or nuances. Example: Scenario criteria is "unstoppable water leak" and the caller reports "constantly running water".
- Low confidence (<0.6): Not the direct intent or significant match. The conversation may have only superficial similarity to the scenario criteria. Example: Scenario criteria is "unstoppable water leak" and the caller says "water is leaking but it is not constant" — related but does not meet the entire criteria.

If the confidence is below 0.6, return scenarioIndex: null (no match).

Respond with a JSON object:
{
  "scenarioIndex": <0-based index of the matching scenario> | null,
  "confidence": <0.0 to 1.0>,
  "reason": "Brief explanation of why this scenario was selected or why none matched"
}`,
    userPrompt: `SCENARIOS:
{{scenarioList}}

CONVERSATION TRANSCRIPT:
{{transcriptText}}`,
    variables: ['scenarioList', 'transcriptText'],
    contentVariable: 'transcriptText',
    variableSchema: z.object({
        transcriptText: z.string().describe('Transcript Text'),
        scenarioList: z.array(z.string()).describe('Scenario List'),
    }),
    responseSchema: ScenarioClassificationResponseSchema,
    evalFields: [
        {
            key: 'scenarioIndex',
            type: 'number',
            extract: (r) => r.scenarioIndex,
        },
        {
            key: 'confidence',
            type: 'number',
            extract: (r) => r.confidence,
        },
    ],
    metadata: {
        description: 'Classifies a conversation transcript against company scenarios',
        model: 'gpt-4o-mini',
        maxTokens: 200,
        responseFormat: 'json_object',
        temperature: 0.1,
    },
};
//# sourceMappingURL=scenario-classification.js.map